import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificationsApi } from '../lib/api'

interface Notification {
  id: string
  type: string
  title: string
  message: string
  status: string
  actionUrl?: string | null
  createdAt: string
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const navigate = useNavigate()

  const [pollInterval, setPollInterval] = useState(30000)
  const rateLimitedRef = useRef(false)
  // Tracks the count across polls so we can tell "a new one arrived" apart from
  // "the count is just what it's always been" -- the latter would otherwise toast
  // on every page load for whatever was already unread from a prior session.
  // Persisted to localStorage (not just a ref) because this component lives inside
  // Navbar, which every page mounts separately -- a plain in-memory ref reset to
  // null on every route navigation, so a notification that arrived and was read
  // by the next poll on a *different* page than the one open when it landed never
  // got detected as "new" at all, silently skipping the toast.
  const LAST_SEEN_COUNT_KEY = 'lastSeenUnreadCount'
  const prevUnreadCountRef = useRef<number | null>((() => {
    try {
      const raw = localStorage.getItem(LAST_SEEN_COUNT_KEY)
      return raw !== null ? parseInt(raw, 10) : null
    } catch {
      return null
    }
  })())

  const fetchUnread = async () => {
    try {
      const res = await notificationsApi.getUnreadCount()
      if (res.data.success) {
        const newCount = res.data.data.count
        if (prevUnreadCountRef.current !== null && newCount > prevUnreadCountRef.current) {
          // Something new landed since the last poll -- fetch just that one so
          // NotificationToast has something to show, without switching this
          // component's own badge count over to a paginated call.
          notificationsApi.getAll({ page: 1, limit: 1, status: 'UNREAD' }).then((listRes) => {
            const latest = listRes.data.success ? listRes.data.data?.[0] : null
            if (latest) window.dispatchEvent(new CustomEvent('notification:new', { detail: latest }))
          }).catch(() => {
            // ignore -- the badge count above is still correct either way
          })
        }
        prevUnreadCountRef.current = newCount
        try { localStorage.setItem(LAST_SEEN_COUNT_KEY, String(newCount)) } catch { /* ignore */ }
        setUnreadCount(newCount)
        // Once we get a successful response, reset backoff if we had throttled.
        if (rateLimitedRef.current) {
          rateLimitedRef.current = false
          setPollInterval(30000)
        }
      }
    } catch (err: any) {
      // Back off on rate limit — slow polling so we don't keep hammering the API.
      if (err?.response?.status === 429) {
        rateLimitedRef.current = true
        setPollInterval((prev) => Math.min(prev * 2, 300000))
      }
    }
  }

  const fetchList = async () => {
    try {
      const res = await notificationsApi.getAll({ page: 1, limit: 10 })
      if (res.data.success) setNotifications(res.data.data || [])
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchUnread()
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(fetchUnread, pollInterval)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [pollInterval])

  useEffect(() => {
    if (open) {
      fetchList()
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleNotificationClick = async (n: Notification) => {
    if (n.status === 'UNREAD') {
      try {
        await notificationsApi.markAsRead(n.id)
        setUnreadCount((c) => Math.max(0, c - 1))
      } catch {
        // ignore
      }
    }
    setOpen(false)
    if (n.actionUrl) navigate(n.actionUrl)
  }

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await notificationsApi.markAllAsRead()
      setUnreadCount(0)
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'READ' })))
    } catch {
      // ignore
    }
  }

  const formatTime = (iso: string) => {
    const date = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHr / 24)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 7) return `${diffDay}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span className="mdi mdi-bell-outline" style={{ fontSize: '1.5rem', color: 'var(--text-secondary)' }} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#E53E3E',
              color: 'white',
              fontSize: '0.625rem',
              fontWeight: 700,
              borderRadius: '9999px',
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            top: 'calc(var(--navbar-height) - 0.75rem)',
            right: '1rem',
            width: '340px',
            maxWidth: 'calc(100vw - 2rem)',
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            zIndex: 1001,
            maxHeight: '480px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.875rem 1rem',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--forest-green)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: n.status === 'UNREAD' ? 'var(--bg-tint)' : 'white',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    {n.status === 'UNREAD' && (
                      <span
                        style={{
                          flexShrink: 0,
                          marginTop: '5px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: 'var(--forest-green)',
                        }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px' }}>
                        {n.title}
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {n.message}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {formatTime(n.createdAt)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false)
              navigate('/notifications')
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'center',
              background: 'none',
              border: 'none',
              borderTop: '1px solid var(--border)',
              padding: '0.7rem 1rem',
              color: 'var(--forest-green)',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  )
}